import { useState, useEffect } from "react";
import { Card, Row, Col, Container } from "react-bootstrap";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
} from "recharts";
import { BsBank } from "react-icons/bs";
import { HiUserGroup } from "react-icons/hi2";
import "../../styles/Dashboard.css";
import TradingViewTimelineWidget from "../tradingViewWidgets/tradingviewNews";
import TradingViewTickerTape from "../tradingViewWidgets/TradingViewTickerTape";
import LockAccount from "../../images/locked-card.png";
import {
  useProfile,
  useBalance,
  useDeposits,
} from "../../services/hooks/getApi";
import Shimmer from "../utilComponents/shimmer";
import { useNavigate } from "react-router-dom";

const Dashboard = () => {
  const [fetchLoading, setFetchLoading] = useState(true);
  const { data: userData, isLoading: userLoading } = useProfile();
  const { data: accountData } = useBalance();
  const { data: depositData } = useDeposits();
  // console.log("user Data:", userData);

  const navigate = useNavigate();

  useEffect(() => {
    if (!userLoading) {
      setFetchLoading(false);
    }
  }, [userLoading]);

  const accountBalance = {
    totalInvestment: accountData?.data?.totalInvestment || 0,
    totalReturns: accountData?.data?.totalReturns || 0,
    currentInvestment: accountData?.data?.currentInvestment || 0,
    currentReturns: accountData?.data?.currentReturns || 0,
  };

  const pieDataTotal = [
    { name: "Total Investment", value: accountBalance.totalInvestment || 0 },
    { name: "Total Returns", value: accountBalance.totalReturns || 0 },
  ];

  const pieDataCurrent = [
    {
      name: "Current Investment",
      value: accountBalance.currentInvestment || 0,
    },
    { name: "Current Returns", value: accountBalance.currentReturns || 0 },
  ];

  const COLORS = ["#D4AF37", "#00C49F"];

  const chartData = (depositData?.deposits || []).map((deposit) => ({
    date: new Date(deposit.requestedAt).toLocaleDateString("en-GB"),
    amount: deposit.amount,
  }));
  // ==========================

  document.title = "Dashboard | Bitstek Advisory";

  return (
    <Container fluid className="dashboard-container">
      {/* Row 1 */}
      <Row>
        <TradingViewTickerTape />
      </Row>
      {/* Row 2 */}
      <Row className="mb-4 ml-10 mr-10 rowTop">
        <Col md={6} className="gif-container" style={{ height: "300px" }}>
          <Card className={`p-3 text-white custom-card first-card ${userData?.isFreezed ? "freezed-card" : ""}`}>
            <Card.Body>
              {userData?.isFreezed && (
              <img src={LockAccount} alt="Locked Account" className="lockIMG" />
              )}
              {userData?.isFreezed && (
                <Card.Text className="text-center text-info">
                  <small>Account Freezed</small>
                </Card.Text>
              )}
              <Row className="first-box-text">
                <Col md={6}>
                  {fetchLoading ? (
                    <Shimmer width="100%" height="35px" />
                  ) : (
                    <Card.Title className="text-center">
                      User Account:{" "}
                      {userData?.AccountID || <small>loading..</small>}
                    </Card.Title>
                  )}
                </Col>
                <Col md={6}>
                  {fetchLoading ? (
                    <Shimmer width="100%" height="35px" />
                  ) : (
                    <Card.Text className="font-bold text-center">
                      KYC Status:{" "}
                      <span
                        className={
                          userData?.KYC_Status === "Approved"
                            ? "text-success"
                            : "text-danger"
                        }>
                        {userData?.KYC_Status === "Approved"
                          ? "Approved"
                          : "❌ KYC Not Verified"}
                      </span>
                    </Card.Text>
                  )}
                </Col>
              </Row>
              <hr />
              <Row className="first-box-text">
                <Col md={12}>
                  {fetchLoading ? (
                    <Shimmer width="100%" height="35px" />
                  ) : (
                    <Card.Title className="text-center">
                      User Name:{" "}
                      {userData?.FullName || <small>loading..</small>}
                    </Card.Title>
                  )}
                </Col>
              </Row>
              <Row className="first-box-text">
                <Col md={12}>
                  {fetchLoading ? (
                    <Shimmer width="100%" height="35px" />
                  ) : (
                    <Card.Title className="text-center">
                      Account Type:{" "}
                      {userData?.Account_Type || <small>loading..</small>}
                    </Card.Title>
                  )}
                </Col>
              </Row>
              <hr />
              <Row className="first-box-text">
                <Col md={6}>
                  {fetchLoading ? (
                    <Shimmer width="100%" height="35px" />
                  ) : (
                    <Card.Text
                      className="text-center"
                      style={{ color: "rgb(226, 194, 94)" }}>
                      Total Investment: ₹{accountBalance.totalInvestment || 0}
                    </Card.Text>
                  )}
                </Col>
                <Col md={6}>
                  {fetchLoading ? (
                    <Shimmer width="100%" height="35px" />
                  ) : (
                    <Card.Text
                      className="text-center"
                      style={{ color: "rgb(226, 194, 94)" }}>
                      Total Returns: ₹{accountBalance.totalReturns || 0}
                    </Card.Text>
                  )}
                </Col>
              </Row>
              <Row className="first-box-text">
                <Col md={6}>
                  {fetchLoading ? (
                    <Shimmer width="100%" height="35px" />
                  ) : (
                    <Card.Text className="text-center">
                      Current Investment: ₹
                      {accountBalance.currentInvestment || 0}
                    </Card.Text>
                  )}
                </Col>
                <Col md={6}>
                  {fetchLoading ? (
                    <Shimmer width="100%" height="35px" />
                  ) : (
                    <Card.Text className="text-center">
                      Current Returns: ₹{accountBalance.currentReturns || 0}
                    </Card.Text>
                  )}
                </Col>
              </Row>
              <hr />
              <Row className="first-box-text">
                <Col md={12}>
                  {fetchLoading ? (
                    <Shimmer width="100%" height="35px" />
                  ) : (
                    <Card.Title className="text-success text-center">
                      <BsBank />
                      <br />
                      <b>
                        Account-Balance: ₹
                        {accountBalance.currentInvestment +
                          accountBalance.currentReturns || 0}
                      </b>
                    </Card.Title>
                  )}
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
        <Col md={6}>
          <Card className="p-3 text-white card transparent-card">
            <Card.Body className="gif-container">
              <iframe title="gif-container" src="https://lottie.host/embed/ab772d72-88ec-4b31-a805-3499a10807d0/yBeImLsp6h.lottie"></iframe>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Row 3 */}

      <Row className="rowTop">
        <Col md={6}>
          <Card className="p-3 text-white transparent-card">
            <Card.Body className="gif-container">
              <iframe title="gif-container" src="https://lottie.host/embed/15ef76ad-42e4-4879-b89e-3d2e9b869d56/PoOVWIEue5.lottie"></iframe>
            </Card.Body>
          </Card>
        </Col>
        <Col md={6}>
          <Card className="p-3 text-white transparent-card custom-card card-height">
            <TradingViewTimelineWidget />
          </Card>
        </Col>
      </Row>

      {/* Row 4   */}
      <Row className="mb-4 rowTop">
        <Col md={6}>
          <Card className="p-3 text-white shadow-lg custom-card card-height">
            <Card.Body>
              <Card.Title>
                Total-Investment | Total-Returns (Investment Breakdown)
              </Card.Title>
              {pieDataTotal.every((item) => item.value === 0) ? (
                <div className="text-center text-warning">
                  Waiting for Deposit
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart className="pie-chart">
                    <Pie
                      data={pieDataTotal}
                      dataKey="value"
                      outerRadius={80}
                      label={({ percent }) => `${(percent * 100).toFixed(0)}%`}>
                      {pieDataTotal.map((_, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={COLORS[index % COLORS.length]}
                        />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </Card.Body>
          </Card>
        </Col>
        <Col md={6}>
          <Card className="p-3 text-white shadow-lg custom-card card-height">
            <Card.Body>
              <Card.Title>
                Current-Investment | Current-Returns (Investment Breakdown)
              </Card.Title>
              {pieDataCurrent.every((item) => item.value === 0) ? (
                <div className="text-center text-warning">
                  Waiting for Deposit
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie
                      data={pieDataCurrent}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      outerRadius={90}
                      innerRadius={50}
                      paddingAngle={5}
                      label={({ percent }) => `${(percent * 100).toFixed(0)}%`}
                      stroke="none">
                      {pieDataCurrent.map((_, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={COLORS[index % COLORS.length]}
                        />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{
                        backgroundColor: "white",
                        color: "black",
                        border: "none",
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Row 5  */}
      <Row className="g-4">
        <Col md={12}>
          <Card className="p-4 h-100 custom-card">
            <h3>Deposits Frequency</h3>
            <Card.Body className="text-primary">
              {chartData.length === 0 ? (
                <div className="text-center text-warning">
                  Waiting for Deposit History
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={chartData}>
                    <CartesianGrid stroke="#ccc" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <Tooltip />
                    <Line
                      type="monotone"
                      dataKey="amount"
                      stroke="#8884d8"
                      strokeWidth={3}
                    />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
      {/* Row 6 */}
      <Row>
        <Col md={12}>
          <Card className="p-3 text-white transparent-card">
            <Card.Body className="gif-container">
              <iframe title="git-container" src="https://lottie.host/embed/7fb073fe-85f9-48bf-95c7-d71b4d50b718/t8GD8QLlyK.lottie"></iframe>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Row 7 - Refer a Friend */}
      <Row className="mb-4 rowTop">
        <Col md={12}>
          <Card
            className="referral-card custom-card"
            onClick={() => navigate("/referral")}>
            <Row className="align-items-center">
              <Col md={2} className="text-center">
                <div className="referral-icon">
                  <HiUserGroup size={60} />
                </div>
              </Col>
              <Col md={10}>
                <h3 className="referral-heading">
                  Refer a Friend & Earn Rewards!
                </h3>
                <p className="referral-text">
                  Invite your friends to Bitstek Advisory and get rewarded when they
                  join. Click here to get your unique referral link.
                </p>
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default Dashboard;
